import * as moment from 'moment';
import { Cookies } from 'react-cookie';
import { useRecoilValue } from 'recoil';
import { ACampaigns } from '../../atoms/campaignAtom';
import { ACompanies } from '../../atoms/clientCompany';
import { AUsers } from '../../atoms/global/users';
import { AProducts } from '../../atoms/product';
import { OnDateChangeType } from '../../components_v2/toolbarFilter/time/DatePicker';
import { URL_FOUNDATION } from '../../config/keys';
import { Dashboard, DashboardChartConfigWithChartData, getCharts, getDashboards } from '../dashboard/action';
import { DashboardState, fetchChartData, GroupedValueNameData, ValueNameData } from '../dashboard/cross_platform/dashboard_script';
import { getLanguage } from '../reports/utils';

type AiChartData = (ValueNameData | GroupedValueNameData[])[];

// Dashboard filters used to compute the archives, so a refresh fetches the same data
export type AiDataFilters = {
	companyId?: number,
	userId?: number,
	startDate?: number,
	endDate?: number,
}

export function toAiDataFilters(companyId: number | undefined, userId: number | undefined, filterDates: OnDateChangeType): AiDataFilters {
	return {
		companyId,
		userId,
		startDate: filterDates.startDate?.valueOf(),
		endDate: filterDates.endDate?.valueOf(),
	};
}

export function chartToAiArchive(chart: DashboardChartConfigWithChartData, data: AiChartData): string {
	let archive: string = '';
	if (chart.data.ai_options?.instructions) archive += `${chart.data.ai_options.instructions}\n`;
	if (chart.name) archive += `chart title: ${chart.name}\n`;
	archive += `chart data: ${chartDataToAiData(data)}\n`;
	return archive;
}

export function dashboardToAiArchive(dashboard: Dashboard | undefined, chartData: Map<string, AiChartData>): string {
	let context = `This is a dashboard with ${dashboard?.charts.length} charts listed with titles, data and how to interpret them:`;
	for (const chart of dashboard?.charts ?? []) {
		const data = chartData.get(chart.uuid);
		if (!data) continue;
		context += `\nchart title: ${chart.name}\ndata: ${chartDataToAiData(data)}\ninterpretation: ${chart.data?.ai_options?.instructions ?? 'no instructions given'}`;
	}
	return context;
}

function chartDataToAiData(chartData: AiChartData): string {
	return JSON.stringify(chartData.map((data) => {
		if (Array.isArray(data)) {
			return data.map((groupedData) => ({
				name: groupedData.name,
				value: groupedData.value,
				group: groupedData.group
			}));
		} else {
			return {
				name: data.name,
				value: data.value
			};
		}
	}));
}

export type AiArchivesFetcher = (target: { chartId?: string, dashboardId?: string, filters?: AiDataFilters }) => Promise<string[] | undefined>;

// Returns a function that fetches the up to date archives of a chart / dashboard from the api
export function useAiArchivesFetcher(): AiArchivesFetcher {
	const users = useRecoilValue(AUsers);
	const products = useRecoilValue(AProducts);
	const companies = useRecoilValue(ACompanies);
	const campaigns = useRecoilValue(ACampaigns);

	return async({ chartId, dashboardId, filters }) => {
		const state: DashboardState = {
			dashboard: null,
			foundationUrl: URL_FOUNDATION,
			authToken: (new Cookies().get('id_token') as string | undefined)?.split(' ')[1] ?? '',
			rawProducts: products,
			users: new Map(users.map(user => [user.id, user.name])),
			products: new Map(products.map(product => [product.uuid, product.name])),
			companies: new Map(Object.entries(companies).map(([key, value]) => [parseInt(key), value])),
			campaigns: new Map(Object.entries(campaigns).map(([key, value]) => [parseInt(key), value.name])),
			companyId: filters?.companyId,
			userId: filters?.userId,
			filterDates: {
				startDate: filters?.startDate !== undefined ? moment(filters.startDate) : null,
				endDate: filters?.endDate !== undefined ? moment(filters.endDate) : null,
			},
			locale: getLanguage(),
		};

		if (chartId !== undefined) {
			const chart = (await getCharts()).find(c => c.uuid === chartId);
			if (!chart) return;
			return [chartToAiArchive(chart, await fetchChartData(chart, state))];
		}
		if (dashboardId !== undefined) {
			const dashboard = (await getDashboards()).find(d => d.uuid === dashboardId);
			if (!dashboard) return;
			const chartData = new Map<string, AiChartData>();
			await Promise.all(dashboard.charts.map(async chart => {
				chartData.set(chart.uuid, await fetchChartData(chart, state));
			}));
			return [dashboardToAiArchive(dashboard, chartData)];
		}
	};
}
